import { Injectable, inject, signal } from '@angular/core';
import { RoutineService } from '../../services/routine.service';
import { TOOL_BY_ID } from '../../shell/shell.models';

/**
 * Single source of truth for the tools in the user's routine across the whole
 * results screen.
 *
 * The per-domain "build your routine" matrices (symptoms / THINK / SENSE) push
 * their selection here on "Add to routine"; the routine panel (paid-0) renders
 * and mutates the same list. So a tool added on any panel shows up on the
 * routine page immediately — they share one reactive list instead of each
 * keeping a private copy.
 *
 * Provided at `ResultsComponent` level (not root). It seeds from her REAL
 * saved routine (the same schedule the app home reads), never from a default
 * starter list, so the tools shown here are exactly the ones the app will show
 * (Danna 26.09).
 */
@Injectable()
export class RoutineStoreService {
  private routine = inject(RoutineService);

  /** ordered tool ids currently in the routine */
  readonly tools = signal<string[]>([]);

  constructor() {
    this.routine
      .getSchedule(true)
      .then((schedule) => {
        const saved = Object.keys(schedule || {}).filter((id) => schedule[id]?.active && TOOL_BY_ID[id]);
        // Keep anything added on this screen while the fetch was in flight.
        this.tools.update((cur) => [...saved, ...cur.filter((id) => !saved.includes(id))]);
      })
      .catch(() => {});
  }

  /** add ids that aren't already present, preserving order */
  add(ids: string[]): void {
    if (!ids.length) return;
    this.tools.update((cur) => {
      const next = [...cur];
      for (const id of ids) if (!next.includes(id)) next.push(id);
      return next;
    });
  }

  remove(id: string): void {
    this.tools.update((cur) => cur.filter((x) => x !== id));
  }

  /** replace the whole list (used by drag reorder) */
  set(ids: string[]): void {
    this.tools.set([...ids]);
  }

  has(id: string): boolean {
    return this.tools().includes(id);
  }
}
